import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { entityIdFromPath, type EntityLinkVerdict, parseEntityLink } from "~/ai-chat/model/entity-link";

export interface ConversationArtifact {
  resourceId: string;
  name: string;
}

export interface VerifiedConversationArtifact extends ConversationArtifact {
  verdict: EntityLinkVerdict;
}

const MARKDOWN_LINK = /\[([^\]]+)\]\(([^)\s]+)\)/g;

// The tracked assets (resources / artifacts) a conversation surfaced, deduped by resource id:
// the entity links the assistant emits — `[name](/resources/<id>)` / `/resources/artifact/<id>`
// — in first-seen order, then the resources saved from this chat, which the assistant doesn't
// necessarily link back. Derived client-side since there is no per-conversation artifact endpoint.
export function collectConversationArtifacts(
  messages: ChatTranscriptItem[],
  savedArtifacts: ConversationArtifact[],
): ConversationArtifact[] {
  const byId = new Map<string, ConversationArtifact>();
  for (const item of messages) {
    if (item.kind !== "message" || item.role !== "assistant") continue;
    for (const [, text, href] of item.content.matchAll(MARKDOWN_LINK)) {
      const link = parseEntityLink(href);
      if (link === null || (link.type !== "artifact" && link.type !== "model")) continue;
      const resourceId = entityIdFromPath(link.path);
      if (resourceId === null || byId.has(resourceId)) continue;
      byId.set(resourceId, { resourceId, name: text.trim() });
    }
  }
  for (const artifact of savedArtifacts) {
    if (!byId.has(artifact.resourceId)) byId.set(artifact.resourceId, artifact);
  }
  return [...byId.values()];
}
